import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../common/Button';
import type { CalibreImage } from '../../services/firebase';

const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

export interface TierManufacturerOption {
  id: string;
  name: string;
}

/**
 * A tier's manufacturers — primary + "also made with" — mirroring the calibre
 * editor's Primary manufacturer select and Co-developed chips.
 *
 * One deliberate difference: a tier's primary is optional (blank = the base
 * calibre's), and extras can be added without one. The extras are the point of
 * a tier's manufacturer list — a brand modification finished by another house —
 * and the app shows them on the calibre screen alongside the base calibre's own.
 */
export function TierManufacturerFields({
  manufacturerId,
  additionalManufacturerIds,
  onChange,
  options,
  loading,
}: {
  manufacturerId: string;
  additionalManufacturerIds: string[];
  onChange: (next: { manufacturerId: string; additionalManufacturerIds: string[] }) => void;
  options: TierManufacturerOption[];
  loading?: boolean;
}) {
  const nameOf = (id: string) => options.find((o) => o.id === id)?.name ?? id;

  const setPrimary = (id: string) =>
    onChange({
      manufacturerId: id,
      // The new primary can't also be an extra.
      additionalManufacturerIds: additionalManufacturerIds.filter((x) => x !== id),
    });
  const addExtra = (id: string) => {
    if (!id || id === manufacturerId || additionalManufacturerIds.includes(id)) return;
    onChange({ manufacturerId, additionalManufacturerIds: [...additionalManufacturerIds, id] });
  };
  const removeExtra = (id: string) =>
    onChange({ manufacturerId, additionalManufacturerIds: additionalManufacturerIds.filter((x) => x !== id) });

  return (
    <div className='space-y-2'>
      <div>
        <label className={labelClass} htmlFor='tier-primary-manufacturer'>
          Primary manufacturer
        </label>
        <select
          id='tier-primary-manufacturer'
          value={manufacturerId}
          onChange={(e) => setPrimary(e.target.value)}
          disabled={loading}
          className={inputClass}
        >
          <option value=''>{loading ? 'Loading manufacturers…' : '— Same as the base calibre —'}</option>
          {/* Keep an id the catalogue no longer lists selectable, so opening and
              saving a tier never silently clears it. */}
          {manufacturerId && !options.some((o) => o.id === manufacturerId) && (
            <option value={manufacturerId}>{manufacturerId}</option>
          )}
          {options.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass} htmlFor='tier-additional-manufacturer'>
          Also made with (optional)
        </label>
        {additionalManufacturerIds.length > 0 && (
          <div className='flex flex-wrap gap-1.5 mb-2' data-testid='tier-additional-manufacturers'>
            {additionalManufacturerIds.map((id) => (
              <span
                key={id}
                className='inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-800 border border-brand-200'
              >
                {nameOf(id)}
                <button
                  type='button'
                  onClick={() => removeExtra(id)}
                  className='text-brand-500 hover:text-brand-700'
                  title='Remove manufacturer'
                  aria-label={`Remove ${nameOf(id)}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <select
          id='tier-additional-manufacturer'
          value=''
          onChange={(e) => addExtra(e.target.value)}
          disabled={loading}
          className={inputClass}
        >
          <option value=''>+ Add a manufacturer…</option>
          {options
            .filter((m) => m.id !== manufacturerId && !additionalManufacturerIds.includes(m.id))
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
        </select>
        <p className='mt-1 text-[11px] text-gray-400'>
          Houses involved in this tier beyond the base calibre&rsquo;s (e.g. a brand modification
          finished by a specialist). Shown on the calibre in the app.
        </p>
      </div>
    </div>
  );
}

/**
 * A tier's own images, uploaded the way the calibre editor uploads its images:
 * bytes into the calibre's `calibres/{id}/images/` folder, stored as a
 * `storagePath` (the shape #680's carry-over copies use). Removing an image
 * only drops it from the tier — the bytes stay, because the calibre save that
 * persists the removal may never happen (Cancel), and a clone can share them.
 */
export function TierImagesField({
  images,
  onChange,
  uploadImage,
  resolveImageUrl,
  fetchImage,
  defaultSource,
  maxImages,
}: {
  images: CalibreImage[];
  onChange: (images: CalibreImage[]) => void;
  /** Upload bytes into the calibre's image folder; resolves the storagePath. Absent ⇒ uploads are unavailable. */
  uploadImage?: (file: Blob, fileName: string) => Promise<string>;
  resolveImageUrl?: (storagePath: string) => Promise<string>;
  fetchImage?: (url: string) => Promise<Blob>;
  defaultSource: string;
  maxImages: number;
}) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [newUrl, setNewUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!resolveImageUrl) return;
    const todo = images.map((i) => i.storagePath).filter((p): p is string => !!p && !(p in urls));
    if (todo.length === 0) return;
    let cancelled = false;
    void Promise.all(
      todo.map(async (p) => [p, await resolveImageUrl(p).catch(() => '')] as const),
    ).then((pairs) => {
      if (!cancelled) setUrls((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      cancelled = true;
    };
  }, [images, urls, resolveImageUrl]);

  const full = images.length >= maxImages;

  const upload = async (blob: Blob, fileName: string) => {
    if (!uploadImage) return;
    setUploading(true);
    try {
      const storagePath = await uploadImage(blob, fileName);
      const c = caption.trim();
      onChange([...images, { storagePath, source: defaultSource, ...(c ? { caption: c } : {}) }]);
      setNewUrl('');
      setCaption('');
    } catch (err) {
      console.error('Failed to upload tier image:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addFromUrl = async () => {
    const url = newUrl.trim();
    if (!url || !fetchImage) return;
    setUploading(true);
    try {
      const blob = await fetchImage(url);
      await upload(blob, url.split('/').pop()?.split('?')[0] || 'image.jpg');
    } catch (err) {
      console.error('Failed to fetch tier image from URL:', err);
      toast.error('Failed to fetch image from URL');
      setUploading(false);
    }
  };

  return (
    <div>
      <div className={labelClass}>
        Tier images ({images.length}/{maxImages})
      </div>
      {images.length > 0 && (
        <div className='flex flex-wrap gap-2 mb-2' data-testid='tier-images'>
          {images.map((img, idx) => {
            const src = img.storagePath ? urls[img.storagePath] || '' : img.url || '';
            return (
              <div
                key={img.storagePath || img.url || idx}
                className='relative border rounded-lg overflow-hidden bg-white'
                style={{ width: 96 }}
              >
                {src ? (
                  <img src={src} alt={img.caption || `Tier image ${idx + 1}`} className='w-full h-20 object-cover' />
                ) : (
                  <div className='w-full h-20 bg-gray-100 flex items-center justify-center text-[10px] text-gray-400'>
                    Loading…
                  </div>
                )}
                {img.caption && (
                  <div className='px-1 py-0.5 truncate text-[10px] text-gray-500' title={img.caption}>
                    {img.caption}
                  </div>
                )}
                <button
                  type='button'
                  onClick={() => onChange(images.filter((_, i) => i !== idx))}
                  className='absolute top-1 right-1 bg-white border rounded-full text-red-500 hover:text-red-700 w-5 h-5 flex items-center justify-center text-xs shadow-sm'
                  title='Remove image from this tier'
                  aria-label={`Remove tier image ${idx + 1}`}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
      {!uploadImage ? (
        <p className='text-[11px] text-gray-400'>Name the calibre before adding tier images.</p>
      ) : (
        !full && (
          <div className='space-y-1.5'>
            <div className='flex gap-2'>
              <input
                type='url'
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder='Image URL…'
                className={`${inputClass} flex-1`}
                aria-label='Tier image URL'
              />
              <input
                type='text'
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder='Caption (optional)'
                className={`${inputClass} flex-1`}
                aria-label='Tier image caption'
              />
              <Button
                variant='secondary'
                size='sm'
                onClick={() => void addFromUrl()}
                disabled={uploading || !newUrl.trim() || !fetchImage}
              >
                {uploading ? 'Uploading…' : 'Add from URL'}
              </Button>
            </div>
            <input
              ref={fileRef}
              type='file'
              accept='image/*'
              aria-label='Upload tier image'
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file, file.name);
              }}
              className='text-xs text-gray-600'
            />
          </div>
        )
      )}
    </div>
  );
}
